'use client';

import { ImageCarousel } from '@woonstad/design-system';
import { getMediaProperties } from '@woonstad/helpers-utils';
import Image from 'next/image';

import { mediaUrlForNextImage } from '../../../../components/aanbod/media-types';
import { getDictionary } from '../../../../consts/dictionaries/get-dictionary';
import { SITES } from '../../../../consts/types';

type CarouselImageData = {
  url?: string | null;
  width?: number | null;
  height?: number | null;
  properties?: {
    altText?: string;
    caption?: string;
    src?: string | { url?: string | null } | null;
  } | null;
};

type CarouselProperties = {
  afbeeldingen?: CarouselImageData[] | null;
};

function createCarouselImage(image: CarouselImageData) {
  return function CarouselImage({ className }: { className?: string }) {
    const rawSrc = getMediaProperties(image);

    if (!rawSrc) {
      return undefined;
    }

    const src = mediaUrlForNextImage(rawSrc.url);

    if (!src) {
      return undefined;
    }

    return (
      <Image
        width={image.width ?? 2000}
        height={image.height ?? 1500}
        src={src}
        alt={image.properties?.altText ?? ''}
        className={className}
        quality={85}
      />
    );
  };
}

type Props = {
  properties: CarouselProperties;
};

export const ComplexCarousel = ({ properties }: Props) => {
  const dict = getDictionary(SITES.Stadswonen);

  if (!properties.afbeeldingen?.length) return null;

  const filteredImages = properties.afbeeldingen
    .reduce((acc, image, currentIndex) => {
      const mediaProps = getMediaProperties(image);

      if (!mediaProps) {
        return acc;
      }

      const carouselImage = createCarouselImage(image);

      if (!carouselImage) {
        return acc;
      }

      return [...acc, { id: `${currentIndex}`, Image: carouselImage, caption: image.properties?.caption }];
    }, [] as { id: string; Image: ReturnType<typeof createCarouselImage>; caption?: string }[]);

  if (!filteredImages.length) return null;

  return (
    <div>
      <ImageCarousel
        images={filteredImages}
        nextSlideLabel={dict['Volgende afbeelding']}
        prevSlideLabel={dict['Vorige afbeelding']}
        indicatorSeparatorLabel={dict['van']}
        closeOverlayLabel={dict['Sluit']}
      />
    </div>
  );
};
